/**
 * Studio 3311 — Theme JavaScript
 */

/* ============================================
   Header: sticky, search, mobile nav
   ============================================ */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.searchToggle = this.querySelector('[data-action="toggle-search"]');
    this.searchDrawer = this.querySelector('.header-search-drawer');
    this.mobileNavToggle = this.querySelector('[data-action="toggle-mobile-nav"]');
    this.mobileNav = this.querySelector('.mobile-nav');
    this.closeSearch = this.querySelector('[data-action="close-search"]');

    this.searchToggle?.addEventListener('click', () => this.toggleSearch());
    this.closeSearch?.addEventListener('click', () => this.closeSearchDrawer());
    this.mobileNavToggle?.addEventListener('click', () => this.toggleMobileNav());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeSearchDrawer();
        this.closeMobileNav();
      }
    });
  }

  toggleSearch() {
    const open = this.searchDrawer.hidden;
    this.searchDrawer.hidden = !open;
    if (open) {
      this.searchDrawer.querySelector('input')?.focus();
    }
  }

  closeSearchDrawer() {
    if (this.searchDrawer) this.searchDrawer.hidden = true;
  }

  toggleMobileNav() {
    const open = this.mobileNav.hidden;
    this.mobileNav.hidden = !open;
    this.mobileNavToggle.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
  }

  closeMobileNav() {
    if (this.mobileNav && !this.mobileNav.hidden) {
      this.mobileNav.hidden = true;
      this.mobileNavToggle?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}

/* ============================================
   Product Gallery: thumbnail switching
   ============================================ */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.featuredImage = this.querySelector('#product-featured-image');
    this.thumbs = this.querySelectorAll('.product-gallery__thumb');

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.switchImage(thumb));
    });
  }

  switchImage(thumb) {
    if (!this.featuredImage) return;
    const src = thumb.dataset.imageSrc;
    const alt = thumb.dataset.imageAlt;
    if (src) {
      this.featuredImage.src = src;
      this.featuredImage.alt = alt || '';
    }
    this.thumbs.forEach(t => t.classList.remove('product-gallery__thumb--active'));
    thumb.classList.add('product-gallery__thumb--active');
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

/* ============================================
   Product Form
   ============================================
   The live product page uses an inline IIFE inside
   `sections/product-main.liquid` with `pm-*` prefixed selectors.
   That script handles variant switching, cart-aware quantity
   validation, and AJAX add-to-cart with inline stock messages.
   No `<product-form>` custom element is used on the site. */

/* ============================================
   Cart: quantity update, item removal
   ============================================ */
class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-action="remove-item"]');
      const incBtn = e.target.closest('[data-action="cart-increment"]');
      const decBtn = e.target.closest('[data-action="cart-decrement"]');

      if (removeBtn) {
        e.preventDefault();
        this.updateQuantity(removeBtn.dataset.line, 0);
      } else if (incBtn) {
        const input = this.querySelector(`input[data-line="${incBtn.dataset.line}"]`);
        if (!input) return;
        const next = (parseInt(input.value, 10) || 0) + 1;
        if (!this.isIncrementAllowed(input, next)) return;
        this.updateQuantity(incBtn.dataset.line, next);
      } else if (decBtn) {
        const input = this.querySelector(`input[data-line="${decBtn.dataset.line}"]`);
        if (input) this.updateQuantity(decBtn.dataset.line, Math.max(0, parseInt(input.value, 10) - 1));
      }
    });
  }

  // Returns false and shows an inline message if the requested quantity
  // would exceed the variant's tracked inventory (when policy is 'deny').
  isIncrementAllowed(input, next) {
    const policy = input.dataset.inventoryPolicy;
    if (!input.dataset.maxQty || policy === 'continue') return true;
    const max = parseInt(input.dataset.maxQty, 10);
    if (!Number.isFinite(max)) return true;
    if (next > max) {
      this.showLineMessage(input, 'Only ' + max + ' available in stock.');
      return false;
    }
    return true;
  }

  showLineMessage(input, text) {
    const host = input.closest('.cart-main__qty-cell') || input.closest('.cart-table__quantity') || input.parentElement;
    if (!host) return;
    let msg = host.querySelector('.cart-main__stock-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'cart-main__stock-msg';
      msg.setAttribute('role', 'status');
      msg.style.cssText = 'font-family:Manrope,sans-serif;font-size:12px;color:#B08D57;margin:6px 0 0;font-weight:500;';
      host.appendChild(msg);
    }
    msg.textContent = text;
    clearTimeout(msg._timer);
    msg._timer = setTimeout(() => { msg && msg.remove(); }, 4000);
  }

  async updateQuantity(line, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity }),
      });
      if (res.ok) {
        window.location.reload();
      } else {
        const err = await res.json().catch(() => ({}));
        const input = this.querySelector(`input[data-line="${line}"]`);
        if (input) this.showLineMessage(input, err.description || 'Could not update quantity.');
      }
    } catch (e) {
      console.error(e);
    }
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

/* ============================================
   Collection: sort
   ============================================ */
document.querySelectorAll('[data-action="sort-collection"]').forEach(select => {
  select.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    window.location.href = url.toString();
  });
});

/* ============================================
   Cart notification close
   ============================================ */
document.querySelectorAll('.cart-notification__close').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelector('#cart-notification').hidden = true;
  });
});

/* ============================================
   Quantity selectors (product page, non-web-component)
   ============================================ */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.quantity-selector__btn');
  if (!btn) return;
  const action = btn.dataset.action;
  if (action !== 'increment' && action !== 'decrement') return;
  const selector = btn.closest('.quantity-selector');
  const input = selector?.querySelector('.quantity-selector__input');
  if (!input) return;
  const delta = action === 'increment' ? 1 : -1;
  const newVal = Math.max(1, parseInt(input.value, 10) + delta);
  input.value = newVal;
});

/* ============================================
   Wishlist heart toggle
   ============================================ */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.product-card__wishlist');
  if (!btn) return;
  const active = btn.getAttribute('aria-pressed') === 'true';
  btn.setAttribute('aria-pressed', active ? 'false' : 'true');
  btn.classList.toggle('is-active', !active);
});

/* ============================================
   Lazy image observer
   ============================================ */
if ('IntersectionObserver' in window) {
  const imgs = document.querySelectorAll('img[loading="lazy"]');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
      }
    });
  });
  imgs.forEach(img => observer.observe(img));
}

/* ============================================
   Collection Grid: filter sidebar toggle
   ============================================ */
(function () {
  const sidebar = document.getElementById('collection-sidebar');
  const overlay = document.querySelector('.collection-sidebar-overlay');
  if (!sidebar) return;

  function openSidebar() {
    sidebar.classList.add('is-open');
    overlay && overlay.classList.add('is-visible');
    document.querySelectorAll('[data-action="toggle-filters"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'true');
    });
    document.body.style.overflow = 'hidden';
  }

  function closeSidebar() {
    sidebar.classList.remove('is-open');
    overlay && overlay.classList.remove('is-visible');
    document.querySelectorAll('[data-action="toggle-filters"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="toggle-filters"]')) {
      sidebar.classList.contains('is-open') ? closeSidebar() : openSidebar();
      return;
    }
    if (e.target.closest('[data-action="close-filters"]')) {
      closeSidebar();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('is-open')) closeSidebar();
  });
})();

/* ============================================
   Collection Grid: filter group accordion
   ============================================ */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-action="toggle-filter-group"]');
  if (!btn) return;
  const group = btn.closest('[data-filter-group]');
  if (!group) return;
  const body = group.querySelector('.filter-group__body');
  if (!body) return;
  const expanded = btn.getAttribute('aria-expanded') === 'true';
  btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  if (expanded) {
    body.style.height = body.scrollHeight + 'px';
    requestAnimationFrame(() => { body.style.height = '0'; });
    body.addEventListener('transitionend', () => { body.hidden = true; body.style.height = ''; }, { once: true });
  } else {
    body.hidden = false;
    body.style.height = '0';
    requestAnimationFrame(() => { body.style.height = body.scrollHeight + 'px'; });
    body.addEventListener('transitionend', () => { body.style.height = ''; }, { once: true });
  }
});

document.querySelectorAll('[data-max-qty]').forEach(input => {
  const max = parseInt(input.dataset.maxQty);
  const policy = input.dataset.inventoryPolicy;
  const form = input.closest('form');
  const msg = form ? form.querySelector('.stock-message') : null;
  const addBtn = form ? form.querySelector('#pm-add-to-cart') : null;

  if (max <= 0 && policy === 'deny' && addBtn) {
    addBtn.disabled = true;
    addBtn.textContent = 'Out of Stock';
    addBtn.style.background = '#F3E7D4';
    addBtn.style.color = '#5A3440';
    addBtn.style.cursor = 'not-allowed';
  }

  input.addEventListener('change', () => {
    if (parseInt(input.value) > max) {
      input.value = max;
      if (msg) {
        msg.textContent = 'Only ' + max + ' left in stock.';
        msg.style.display = 'block';
      }
    } else {
      if (msg) msg.style.display = 'none';
    }
  });
});
